import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type LevelItem = { id: string; name: string };

export type ClassTypeSalesReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  levelId?: string;
  sortBy?: "count" | "revenue";
};

export function ClassTypeSalesReportFilter() {
  const nav = useNavigate({ from: "/app/reports/class-type-sales" });
  const search = useSearch({
    from: "/app/reports/class-type-sales",
  }) as ClassTypeSalesReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [levelId, setLevelId] = useState(search.levelId ?? "");
  const [sortBy, setSortBy] = useState<"count" | "revenue">(
    search.sortBy ?? "revenue",
  );

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setLevelId(search.levelId ?? "");
    setSortBy(search.sortBy ?? "revenue");
  }, [
    search.dateFrom,
    search.dateTo,
    search.levelId,
    search.sortBy,
    defaultFrom,
    defaultTo,
  ]);

  const hasActiveFilters =
    levelId ||
    search.dateFrom ||
    search.dateTo ||
    (search.sortBy && search.sortBy !== "revenue");

  const applyFilters = (updates: Partial<ClassTypeSalesReportSearch> = {}) => {
    const next: ClassTypeSalesReportSearch = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      levelId:
        updates.levelId !== undefined
          ? updates.levelId
          : levelId
            ? levelId
            : undefined,
      sortBy: updates.sortBy ?? sortBy,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີຈ່າຍ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<LevelItem>
            queryKey={["master", "levels", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={levelId}
            onValueChange={(val) => {
              setLevelId(val);
              applyFilters({ levelId: val || undefined });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <SimpleSelect
            value={sortBy}
            onValueChange={(val) => {
              setSortBy(val as "count" | "revenue");
              applyFilters({ sortBy: val as "count" | "revenue" });
            }}
            options={[
              { value: "revenue", label: "ຍອດຂາຍສູງສຸດ" },
              { value: "count", label: "ຈຳນວນຂາຍສູງສຸດ" },
            ]}
            placeholder="ຈັດລຽງ"
            className="h-8 w-full sm:w-40"
          />
        </div>

        {hasActiveFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setLevelId("");
              setSortBy("revenue");
              nav({ search: {} });
            }}
          >
            <XIcon className="mr-1 size-4" />
            ລ້າງຕົວກອງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
